import {
  Home,
  FolderKanban,
  Briefcase,
  TrendingUp,
  FileText,
  BarChart3,
  Calendar,
  Settings,
  FolderOpen,
  BookUser,
  BadgeInfo,
  FileBarChart,
  ClipboardList,
  FolderTree,
  GraduationCap,
  Shield,
  Bell,
  Zap,
  /* FlaskConical, // hidden for phase 1 release (Feature Labs) */
  SlidersHorizontal,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavigationItem {
  id: string;
  label: string;
  description?: string;
  icon?: LucideIcon;
  path?: string;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  title?: string;
  items: NavigationItem[];
}

export interface NavigationConfig {
  sections: NavigationSection[];
}

/**
 * Navigation Configuration
 *
 * Single source of truth for the application's navigation structure.
 * Used by the Breadcrumbs component to derive the breadcrumb trail
 * from the current route path.
 */
export const navigationConfig: NavigationConfig = {
  sections: [
    // Main Navigation
    {
      id: 'main',
      items: [
        {
          id: 'dashboard',
          label: 'Homepage',
          icon: Home,
          path: '/dashboard',
        },
        {
          id: 'projects',
          label: 'Projects',
          icon: FolderKanban,
          path: '/projects',
        },
        {
          id: 'project-templates',
          label: 'Project Templates',
          icon: FolderTree,
          path: '/project-templates',
        },
        {
          id: 'portfolio',
          label: 'Portfolio',
          icon: Briefcase,
          path: '/portfolio',
        },
      ],
    },

    // Resources
    {
      id: 'resources',
      title: 'Resources',
      items: [
        {
          id: 'resources-menu',
          label: 'Resources',
          icon: FolderOpen,
          children: [
            {
              id: 'directory',
              label: 'Directory',
              icon: BookUser,
              path: '/directory',
            },
            {
              id: 'roles-rates',
              label: 'Roles & Rates',
              icon: BadgeInfo,
              path: '/roles-rates',
            },
            {
              id: 'utilisation',
              label: 'Utilisation',
              icon: TrendingUp,
              path: '/utilisation',
            },
            {
              id: 'skills',
              label: 'Skills',
              icon: GraduationCap,
              path: '/skills',
            },
          ],
        },
      ],
    },

    // Reports
    {
      id: 'reports',
      title: 'Reports',
      items: [
        {
          id: 'reports-menu',
          label: 'Reports',
          icon: FileBarChart,
          children: [
            {
              id: 'project-transactions',
              label: 'Project Transactions',
              icon: ClipboardList,
              path: '/project-transactions',
            },
            {
              id: 'wip-reporting',
              label: 'WIP',
              icon: FileText,
              path: '/wip-reporting',
            },
            {
              id: 'audit-log',
              label: 'Audit Log',
              icon: FileBarChart,
              path: '/audit-log',
            },
          ],
        },
      ],
    },

    // Financial
    {
      id: 'financial',
      title: 'Financial',
      items: [
        {
          id: 'budgets',
          label: 'Budgets & Variance',
          icon: BarChart3,
          path: '/budgets',
        },
      ],
    },

    // Planning
    {
      id: 'planning',
      title: 'Planning',
      items: [
        {
          id: 'gantt',
          label: 'Gantt Charts',
          icon: Calendar,
          path: '/gantt',
        },
      ],
    },

    // Administration
    {
      id: 'admin',
      title: 'Administration',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: Settings,
          children: [
            {
              id: 'settings-general',
              label: 'General',
              description: 'Company details and general application settings',
              icon: Settings,
              path: '/settings/general',
            },
            {
              id: 'settings-project',
              label: 'Project',
              description: 'Project creation, templates, budgets, and field constraints',
              icon: SlidersHorizontal,
              path: '/settings/project',
            },
            {
              id: 'settings-integrations',
              label: 'Integrations',
              description: 'Manage company integrations and connections',
              icon: Zap,
              path: '/settings/integrations',
            },
            {
              id: 'settings-user-permissions',
              label: 'Roles & Permissions',
              description: 'Manage user roles and access control',
              icon: Shield,
              path: '/settings/user-permissions',
            },
            {
              id: 'settings-templates',
              label: 'Templates',
              description: 'Invoice layouts and email templates',
              icon: FileText,
              path: '/settings/templates',
            },
            {
              id: 'settings-notifications',
              label: 'Notifications',
              description: 'Configure notification preferences',
              icon: Bell,
              path: '/settings/notifications',
            },
            /* Feature Labs hidden for phase 1 release */
            /* {
              id: 'settings-feature-labs',
              label: 'Feature Labs',
              description: 'Preview and enable upcoming features',
              icon: FlaskConical,
              path: '/settings/feature-labs',
            }, */
          ],
        },
      ],
    },
  ],
};

/**
 * Helper function to get breadcrumb trail for a given path.
 * Always starts after Homepage (Homepage is rendered separately in the component).
 * Traverses the navigation config to find the matching path and returns the hierarchy.
 */
export const getBreadcrumbs = (currentPath: string): NavigationItem[] => {
  // If we're on the dashboard (homepage), return empty array
  if (currentPath === '/dashboard' || currentPath === '/') {
    return [];
  }

  const breadcrumbs: NavigationItem[] = [];

  const findPath = (items: NavigationItem[], path: string, trail: NavigationItem[] = []): boolean => {
    for (const item of items) {
      const newTrail = [...trail, item];

      if (item.path === path) {
        breadcrumbs.push(...newTrail);
        return true;
      }

      if (item.children && findPath(item.children, path, newTrail)) {
        return true;
      }
    }
    return false;
  };

  navigationConfig.sections.forEach((section) => {
    findPath(section.items, currentPath);
  });

  // Handle project detail pages: /projects/:projectId
  if (breadcrumbs.length === 0 && currentPath.startsWith('/projects/')) {
    navigationConfig.sections.forEach((section) => {
      findPath(section.items, '/projects');
    });
    if (breadcrumbs.length > 0) {
      breadcrumbs.push({
        id: 'project-detail',
        label: 'Project Details',
        path: currentPath,
      });
    }
  }

  // Handle settings hub: /settings → redirect to /settings/general
  if (breadcrumbs.length === 0 && currentPath === '/settings') {
    breadcrumbs.push({
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      path: '/settings/general',
    });
  }

  // Filter: keep items with a path, or parent items with children (to show hierarchy)
  const filteredBreadcrumbs = breadcrumbs.filter(item => {
    if (item.path) return true;
    return item.children && item.children.length > 0;
  });

  return filteredBreadcrumbs;
};
